import React, { useState } from 'react'
import {
  Box,
  Button,
  ButtonGroup,
  Grid2 as Grid,
  IconButton,
  InputAdornment,
  TablePagination,
  TextField,
  useTheme,
} from '@mui/material'
import useCustomStyles from '../../hooks/CustomStylesHook'
import SearchIcon from '@mui/icons-material/Search'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import ViewModuleIcon from '@mui/icons-material/ViewModule'
import Colors from '../../utils/colors.json'
import StatusCard from '../../components/StatusCard'
import { ReactComponent as ProgressIcon } from '../../assets/icons/Inprogress.svg'
import LabDetailsCard from './LabDetailsCard'
import { CARD_LAYOUT_OPTIONS } from '../../constants/constants'
import { ReactComponent as ApprovalPendingIcon } from '../../assets/icons/Pending.svg'
import HistoryIcon from '@mui/icons-material/History'
import LoopIcon from '@mui/icons-material/Loop'

const styles = (theme: any) => ({
  peopleContainer: {
    // border: '1px solid grey',
    // borderRadius: '20px 20px 20px 20px',
    flex: 1,
    overflowY: 'auto',
  },
  filterBtn: {
    color: theme.palette.primary.main,
    background: '#F1F1F1',
    borderRadius: 20,
    textTransform: 'capitalize',
  },
  searchField: {
    fontSize: '12px !important',
    borderRadius: 20,
    height: 30,
  },
  dialogActions: {
    justifyContent: 'center',
  },
  dialogCloseBtn: {
    position: 'absolute',
    right: 8,
    top: 8,
    color: theme.palette.grey[500],
  },
  assignedToDialog: {
    backgroundColor: `${theme.palette.primary.light}`,
    display: 'flex',
    flexDirection: 'column',
  },
  assignedToSubmitBtn: {
    borderRadius: '18px',
    textTransform: 'capitalize',
    background: theme.customColors.btnGreen,
  },
  assignedToChangeBtn: {
    textTransform: 'capitalize',
    textDecoration: 'underline',
  },
  formTextField: {
    fontSize: '14px',
    fontWeight: 400,
    color: Colors.TEXT_COLOR_SECONDARY,
    height: 40,
    borderRadius: '20px',
  },
  assignedBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row',
    border: '1px solid #D0D0D0',
    borderRadius: '20px',
    width: '70%',
    flexWrap: 'wrap',
    background: theme.customColors.button,
  },
  scrollContentContainer: {
    flex: 1,
    overflowY: 'auto',
    paddingRight: theme.spacing(2),
  },
  cardPagination: {
    '& .MuiTablePagination-toolbar': {
      minHeight: 0,
      padding: '0 16px',
    },
  },
  selectedLayoutBtn: {
    color: Colors.WHITE_BACKGROUND,
    background: theme.palette.primary.main,
  },
})

const LabTest = () => {
  const theme = useTheme()
  const classes = useCustomStyles(styles, theme)

  const filterCards = [
    {
      title: 'Pending Sample Collections',
      icon: <ApprovalPendingIcon fontSize='small' />,
      count: 5,
      percentage: 2.5,
    },
    {
      // eslint-disable-next-line quotes
      title: "Tomorrow's Scheduled Orders",
      icon: <ProgressIcon />,
      count: 4,
      percentage: 2.5,
    },
    {
      title: 'Rescheduled Orders',
      icon: <HistoryIcon fontSize='small' />,
      count: 28,
      percentage: 2.5,
    },
    {
      title: 'Samples Awaiting Processing',
      icon: <LoopIcon fontSize='small' />,
      count: 20,
      percentage: -2.5,
    },
  ]

  const [cardLayout, setCardLayout] = useState(CARD_LAYOUT_OPTIONS.LIST)
  const [selectedFilterCard, setSelectedFilterCard] = useState(
    'Pending Sample Collections'
  )

  const [page, setPage] = React.useState(2)
  const [rowsPerPage, setRowsPerPage] = React.useState(10)

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  return (
    <Grid
      container
      height={'100%'}
      width={'100%'}
      direction={'column'}
      spacing={2}
      // overflow={'hidden'}
    >
      <Grid container direction={'row'} spacing={1.5}>
        {filterCards?.map((item, index) => (
          <Grid size={{ xs: 3 }} key={index}>
            <StatusCard
              icon={item?.icon}
              title={item?.title}
              count={item?.count}
              percentage={item?.percentage}
              selected={selectedFilterCard === item?.title ? true : false}
              onClick={() => setSelectedFilterCard(item?.title)}
            />
          </Grid>
        ))}
      </Grid>
      <Grid
        container
        spacing={5}
        direction={'row'}
        pr={2}
        position={'sticky'}
        top={0}
        zIndex={1}
        size={{ xs: 12 }}
      >
        <Grid size={{ xs: 12, sm: 12, lg: 6, md: 6, xl: 6 }}>
          <Box
            display={'flex'}
            flexDirection={'row'}
            justifyContent={'start'}
            flexWrap={'wrap'}
            rowGap={2}
            gap={4}
          >
            <Box>All Orders</Box>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, sm: 12, lg: 6, md: 6, xl: 6 }}>
          <Box
            display={'flex'}
            flexDirection={'row'}
            justifyContent={'end'}
            flexWrap={'wrap'}
            gap={4}
            rowGap={2}
          >
            <TextField
              placeholder='Search for Customers'
              type='search'
              size='small'
              slotProps={{
                input: {
                  className: classes?.searchField,
                  startAdornment: (
                    <InputAdornment position='start'>
                      <IconButton size='small'>
                        <SearchIcon fontSize='small' />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Button
              variant='contained'
              className={classes?.filterBtn}
              startIcon={<FilterAltIcon />}
              size='small'
            >
              Filter
            </Button>
            <ButtonGroup>
              <Button
                variant='contained'
                className={`${classes?.filterBtn} ${
                  cardLayout === CARD_LAYOUT_OPTIONS.GRID
                    ? classes?.selectedLayoutBtn
                    : ''
                }`}
                size='small'
                onClick={() => setCardLayout(CARD_LAYOUT_OPTIONS.GRID)}
              >
                <ViewModuleIcon fontSize='small' />
              </Button>
              <Button
                variant='contained'
                className={`${classes?.filterBtn}  ${
                  cardLayout === CARD_LAYOUT_OPTIONS.LIST
                    ? classes?.selectedLayoutBtn
                    : ''
                }`}
                size='small'
                onClick={() => setCardLayout(CARD_LAYOUT_OPTIONS.LIST)}
              >
                <FormatListBulletedIcon fontSize='small' />
              </Button>
            </ButtonGroup>
          </Box>
        </Grid>
      </Grid>
      <Grid
        className={classes?.peopleContainer}
        size={{ xs: 12 }}
        pr={2}
        direction={'column'}
      >
        {cardLayout === CARD_LAYOUT_OPTIONS.LIST ? (
          <Grid container spacing={1.5} direction={'column'} size={{ xs: 12 }}>
            {Array.from({ length: 5 }).map((_, index) => (
              <LabDetailsCard key={index} />
            ))}
          </Grid>
        ) : null}
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TablePagination
          className={classes?.cardPagination}
          component='div'
          count={100}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Grid>
    </Grid>
  )
}

export default LabTest
